/**
 * TextSettingsPopover — a gear in the editor topbar that opens quick text
 * settings (font, size, spacing, drop cap, and reading toggles).
 *
 * On MOBILE renders as a vaul bottom Drawer (content-sized).
 * On DESKTOP renders as a Popover anchored below the trigger.
 */
import { useTranslation } from 'react-i18next';
import { Settings2, Minus, Plus } from 'lucide-react';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from './ui/drawer';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useIsMobile } from '../hooks/use-mobile';

type FontFamily = 'serif' | 'sans' | 'mono';
type LineHeight = 'compact' | 'normal' | 'relaxed';

export interface TextSettingsValues {
  fontFamily: FontFamily;
  fontSize: number;
  lineHeight: LineHeight;
  dropCaps: boolean;
  spellcheck: boolean;
  typewriterMode: boolean;
  showChapterNumber: boolean;
  showWordCount: boolean;
  canvasTheme: 'night' | 'paper';
  writingEffect: 'petals' | 'stardust' | 'inkrain' | 'sparks' | 'glitch' | 'mist' | 'fire' | 'fireflies' | 'neon' | 'glyphs' | 'none';
}

interface TextSettingsPopoverProps {
  settings: TextSettingsValues;
  update: (patch: Partial<TextSettingsValues>) => void;
}

const MIN_SIZE = 14;
const MAX_SIZE = 26;

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; cls?: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/60 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
            o.cls,
            value === o.value
              ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-foreground">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function TextSettingsPopover({ settings, update }: TextSettingsPopoverProps) {
  const { t } = useTranslation();
  const { fontFamily, fontSize, lineHeight } = settings;
  const isMobile = useIsMobile();

  const WRITING_EFFECTS: { value: TextSettingsValues['writingEffect']; label: string }[] = [
    { value: 'petals',    label: t('textSettings.effectPetals', 'Pétalas') },
    { value: 'stardust',  label: t('textSettings.effectStardust', 'Poeira Estelar') },
    { value: 'inkrain',   label: t('textSettings.effectInkRain', 'Chuva de Tinta') },
    { value: 'sparks',    label: t('textSettings.effectSparks', 'Faíscas de Choque') },
    { value: 'glitch',    label: t('textSettings.effectGlitch', 'Faísca Glitch') },
    { value: 'mist',      label: t('textSettings.effectMist', 'Névoa Cósmica') },
    { value: 'fire',      label: t('textSettings.effectFire', 'Lareira em Chamas') },
    { value: 'fireflies', label: t('textSettings.effectFireflies', 'Vagalumes') },
    { value: 'neon',      label: t('textSettings.effectNeon', 'Neon Elétrico') },
    { value: 'glyphs',    label: t('textSettings.effectGlyphs', 'Eco Literário') },
    { value: 'none',      label: t('textSettings.effectNone', 'Nenhum') },
  ];

  const FONTS: { value: FontFamily; label: string; cls: string }[] = [
    { value: 'serif', label: t('textSettings.fontSerif', 'Serifada'), cls: 'font-serif' },
    { value: 'sans', label: t('textSettings.fontSans', 'Sem serifa'), cls: 'font-sans' },
    { value: 'mono', label: t('textSettings.fontMono', 'Mono'), cls: 'font-mono' },
  ];

  const SPACINGS: { value: LineHeight; label: string }[] = [
    { value: 'compact', label: t('textSettings.spacingCompact', 'Compacto') },
    { value: 'normal', label: t('textSettings.spacingNormal', 'Normal') },
    { value: 'relaxed', label: t('textSettings.spacingRelaxed', 'Amplo') },
  ];

  /** Shared settings body rendered inside both the Popover and the Drawer. */
  const settingsBody = (
    <>
      <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-primary">
        {t('textSettings.title', 'Configurações de texto')}
      </h3>

      {/* Canvas theme — the signature paper/night toggle for the writing page */}
      <div className="space-y-1.5">
        <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('textSettings.page', 'Página')}
        </label>
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted/60 p-1">
          {([['night', t('textSettings.night', 'Noite')], ['paper', t('textSettings.paper', 'Papel')]] as const).map(([val, lbl]) => (
            <button
              key={val}
              type="button"
              onClick={() => update({ canvasTheme: val })}
              className={cn(
                'rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                settings.canvasTheme === val
                  ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Writing effect — particles emitted at the caret while typing */}
      <div className="space-y-1.5">
        <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('textSettings.writingEffectLabel', 'Efeito ao escrever')}
        </label>
        <Select
          value={
            WRITING_EFFECTS.some((e) => e.value === settings.writingEffect)
              ? settings.writingEffect
              : 'stardust'
          }
          onValueChange={(v) => update({ writingEffect: v as TextSettingsValues['writingEffect'] })}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WRITING_EFFECTS.map((e) => (
              <SelectItem key={e.value} value={e.value}>
                {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Font family */}
      <div className="space-y-1.5">
        <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('textSettings.font', 'Fonte')}
        </label>
        <Segmented options={FONTS} value={fontFamily} onChange={(v) => update({ fontFamily: v })} />
      </div>

      {/* Font size */}
      <div className="space-y-1.5">
        <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('textSettings.fontSizeLabel', 'Tamanho —')} <span className="text-foreground">{fontSize}px</span>
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => update({ fontSize: Math.max(MIN_SIZE, fontSize - 1) })}
            disabled={fontSize <= MIN_SIZE}
            className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
            aria-label={t('textSettings.decreaseFontSize', 'Diminuir fonte')}
          >
            <Minus size={14} />
          </button>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${((fontSize - MIN_SIZE) / (MAX_SIZE - MIN_SIZE)) * 100}%` }}
            />
          </div>
          <button
            type="button"
            onClick={() => update({ fontSize: Math.min(MAX_SIZE, fontSize + 1) })}
            disabled={fontSize >= MAX_SIZE}
            className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
            aria-label={t('textSettings.increaseFontSize', 'Aumentar fonte')}
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Line height */}
      <div className="space-y-1.5">
        <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t('textSettings.spacing', 'Espaçamento')}
        </label>
        <Segmented options={SPACINGS} value={lineHeight} onChange={(v) => update({ lineHeight: v })} />
      </div>

      {/* Toggles */}
      <div className="space-y-2.5 border-t border-border pt-3">
        <ToggleRow
          label={t('textSettings.dropCaps', 'Capitular (letra inicial)')}
          checked={settings.dropCaps}
          onChange={(v) => update({ dropCaps: v })}
        />
        <ToggleRow
          label={t('textSettings.typewriterMode', 'Máquina de escrever')}
          checked={settings.typewriterMode}
          onChange={(v) => update({ typewriterMode: v })}
        />
        <ToggleRow
          label={t('textSettings.spellcheck', 'Corretor ortográfico')}
          checked={settings.spellcheck}
          onChange={(v) => update({ spellcheck: v })}
        />
        <ToggleRow
          label={t('textSettings.chapterNumber', 'Número do capítulo')}
          checked={settings.showChapterNumber}
          onChange={(v) => update({ showChapterNumber: v })}
        />
        <ToggleRow
          label={t('textSettings.wordCount', 'Contador de palavras')}
          checked={settings.showWordCount}
          onChange={(v) => update({ showWordCount: v })}
        />
      </div>
    </>
  );

  /** Shared trigger button appearance */
  const triggerClassName =
    'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  if (isMobile) {
    return (
      <Drawer>
        <DrawerTrigger
          title={t('textSettings.title', 'Configurações de texto')}
          aria-label={t('textSettings.title', 'Configurações de texto')}
          className={triggerClassName}
        >
          <Settings2 size={17} />
        </DrawerTrigger>

        <DrawerContent className="p-0">
          <DrawerTitle className="sr-only">{t('textSettings.title', 'Configurações de texto')}</DrawerTitle>
          {/* Content-sized: no fixed height — drawer grows to fit settings */}
          <div className="space-y-4 p-4 pb-6">
            {settingsBody}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover>
      <PopoverTrigger
        title={t('textSettings.title', 'Configurações de texto')}
        aria-label={t('textSettings.title', 'Configurações de texto')}
        className={triggerClassName}
      >
        <Settings2 size={17} />
      </PopoverTrigger>

      <PopoverContent side="bottom" align="end" className="w-72 space-y-4 p-4">
        {settingsBody}
      </PopoverContent>
    </Popover>
  );
}
